import {
  useCreateUnifiedField,
  useSearchMetadataFields,
} from '@modules/ManageUnifiedFields/hooks';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { Box, CircularProgress, Stack } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import { MetadataDataType } from 'generated/graphql';
import { useEffect, useState } from 'react';
import { CreateNewUnifiedFieldDrawerFooter } from '../CreateNewUnifiedFieldDrawerFooter';
import { CreateNewUnifiedFieldDrawerHeader } from '../CreateNewUnifiedFieldDrawerHeader';
import { CreateNewUnifiedFieldForm } from '../CreateNewUnifiedFieldForm';

interface CreateUnifiedFieldDrawerProps {
  open: boolean;
  onClose: () => void;
  metadataKey?: string;
  onSubmit?: () => void;
}

export const CreateUnifiedFieldDrawer = ({
  open,
  onClose,
  metadataKey,
  onSubmit,
}: CreateUnifiedFieldDrawerProps) => {
  const [
    queryMetadataFields,
    {
      searchMetadataFields: selectedMetadataField,
      loading: isMetadataFieldLoading,
    },
  ] = useSearchMetadataFields();

  useEffect(() => {
    if (metadataKey) {
      queryMetadataFields({
        key: metadataKey,
      });
    }
  }, [metadataKey, queryMetadataFields]);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<MetadataDataType>();
  const [referencedKeys, setReferencedKeys] = useState<string[]>([]);
  const [shouldHideReferencedKeys, setShouldHideReferencedKeys] =
    useState(true);
  const { showSuccessNotification } = useNotificationContext();

  const [createNewUnifiedField, { loading }] = useCreateUnifiedField();

  const resetUnifiedFieldState = () => {
    setName('');
    setDescription('');
    setReferencedKeys([]);
    setShouldHideReferencedKeys(true);
  };

  const createUnifiedField = async () => {
    try {
      const resp = await createNewUnifiedField({
        name,
        description,
        referencedKeys,
        shouldHideReferencedKeys,
        type,
      });

      if (resp.errors) {
        throw new Error('Failed to create Unified Field.');
      }
      showSuccessNotification({
        contentComponent: 'It would be available for use after ~30 mins.',
        props: {
          title: `New Unified Field ${name} successfully created `,
        },
      });
      resetUnifiedFieldState();
      onSubmit?.();
      onClose();
    } catch {}
  };

  const disableCreateButton =
    referencedKeys.length < 2 || !name?.trim() || !description?.trim() || !type;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: 800,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isMetadataFieldLoading ? (
          <Stack justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
          </Stack>
        ) : (
          <Stack direction="column" height="100%">
            <CreateNewUnifiedFieldDrawerHeader />
            <CreateNewUnifiedFieldForm
              name={name}
              setName={setName}
              description={description}
              setDescription={setDescription}
              setReferencedKeys={setReferencedKeys}
              setShouldHideReferencedKeys={setShouldHideReferencedKeys}
              type={type}
              setType={setType}
              selectedMetadataField={selectedMetadataField}
            />
            <CreateNewUnifiedFieldDrawerFooter
              onClose={onClose}
              createUnifiedField={createUnifiedField}
              loading={loading}
              disableCreateButton={disableCreateButton}
            />
          </Stack>
        )}
      </Box>
    </Drawer>
  );
};
